import { getClassNames } from '@websolutespa/bom-core';
import { useAutosizeTextArea, useLabel } from '@websolutespa/bom-mixer-hooks';
import { IconLlmMicrophone, IconLlmPaperclip, IconLlmRefresh, IconLlmSend, IconLlmStop, IconLlmVolume, IconLlmVolumeX, IconX } from '@websolutespa/bom-mixer-icons';
import { useRef } from 'react';
import { useLlm } from '../../../useLlm/useLlm';
import { useLlmView } from '../../../useLlm/useLlmView';

export const Prompt: React.FC = () => {
  const label = useLabel();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const {
    Blob,
    BusyIndicator,
    DisclaimerFooter,
    PoweredBy,
    Suggestions,
  } = useLlm(state => state.components);
  const app = useLlm(state => state.app);
  const threadId = useLlm(state => state.threadId);
  const prompt = useLlm(state => state.prompt);
  const upload = useLlm(state => state.upload);
  const messages = useLlm(state => state.messages);
  const streaming = useLlm(state => state.streaming);
  const speakEnabled = useLlm(state => state.speakEnabled);
  const mimeTypes = useLlm(state => state.mimeTypes);
  const storageMode = useLlm(state => state.storageMode);
  const { clear, setPrompt, recognizeStart, recognizeStop, abort, toggleSpeak, removeFile, hasSpeechRecognitionSupport, hasSpeechSynthesisSupport, addFile, hasUploadSupport } = useLlm(state => state.actions);
  const { send } = useLlmView(state => state.actions);

  useAutosizeTextArea(textAreaRef.current, prompt);

  const onChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const prompt = event.target?.value;
    setPrompt(prompt);
  };

  const onSubmit = async (event?: React.FormEvent) => {
    event && event.preventDefault();
    if (!prompt) {
      return;
    }
    await send(prompt, (response) => {
      // console.log('Prompt.onChunk', response.chunks);
    }, (response) => {
      // console.log('Prompt.onEnd', response);
    });
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.code == 'Enter' && !event.shiftKey) {
      onSubmit(event);
    }
  };

  const hasSpeechSynthesis = hasSpeechSynthesisSupport();
  const hasSpeechRecognition = hasSpeechRecognitionSupport();
  const hasUpload = hasUploadSupport();

  return (
    <div className="llm__prompt">
      <div className="llm__container">
        {messages.length === 0 && (
          <Suggestions />
        )}
        <section id="llm-prompt">
          <form className="llm__prompt-form" onSubmit={onSubmit}>
            <div className={getClassNames('llm__prompt-input', {
              '-speech-synthesis': hasSpeechSynthesis,
              '-speech-recognition': hasSpeechRecognition,
              '-streaming': streaming,
            })}>
              {hasSpeechSynthesis && (
                <button className="llm__prompt-speak" type="button"
                  aria-label={speakEnabled ? label('llm.disableSpeak') : label('llm.enableSpeak')}
                  title={speakEnabled ? label('llm.disableSpeak') : label('llm.enableSpeak')}
                  aria-pressed={speakEnabled}
                  onClick={toggleSpeak}
                >
                  {speakEnabled ? <IconLlmVolume /> : <IconLlmVolumeX />}
                </button>
              )}
              {storageMode === 'session' && threadId && (
                <button className="llm__prompt-speak" type="button"
                  aria-label={label('llm.newThread')}
                  title={label('llm.newThread')}
                  onClick={clear}
                >
                  <IconLlmRefresh />
                </button>
              )}
              <div className="llm__prompt-textareaGroup">
                <label htmlFor="llm-prompt-textarea" className="llm__prompt-label">{label('llm.prompt')}</label>
                <textarea id="llm-prompt-textarea" className="llm__prompt-textarea" name="prompt" placeholder={app?.contents.promptPlaceholder} onKeyDown={onKeyDown} onChange={onChange} ref={textAreaRef} rows={1} value={prompt} aria-disabled={streaming}></textarea>
                <BusyIndicator />
              </div>
              {hasUpload && (
                <>
                  <div className="llm__prompt-upload-group">
                    <div className="llm__prompt-upload"
                      aria-label={label('llm.upload')}
                      title={label('llm.upload')}
                    >
                      <IconLlmPaperclip />
                    </div>
                    <input className="llm__prompt-upload-input" type="file" id="file-input" accept={mimeTypes} onChange={addFile} />
                  </div>
                  {upload && (
                    <button className="llm__prompt-upload-file" onClick={() => removeFile()} title={upload.file.name}>
                      <span>{upload.file.name}</span>
                      <IconX />
                      <img src={upload.base64} alt="" />
                    </button>
                  )}
                </>
              )}
              {hasSpeechRecognition && (
                <button className="llm__prompt-microphone" type="button"
                  aria-label={label('llm.dictate')}
                  title={label('llm.dictate')}
                  onMouseDown={() => recognizeStart()}
                  onMouseUp={() => recognizeStop()}
                  onTouchStart={() => recognizeStart()}
                  onTouchEnd={() => recognizeStop()}
                >
                  <IconLlmMicrophone />
                </button>
              )}
            </div>
            {streaming ? (
              <button className="llm__prompt-stop" type="button"
                disabled={!threadId}
                aria-disabled={!threadId}
                aria-label={label('llm.stopResponse')}
                onClick={abort}
              >
                <Blob />
                <i><IconLlmStop /></i>
              </button>
            ) : (
              <button className="llm__prompt-submit" type="submit"
                aria-disabled={!prompt}
                aria-label={label('llm.sendPrompt')}
              >
                <Blob />
                <i><IconLlmSend /></i>
              </button>
            )}
          </form>
        </section>
      </div>
      <nav className="llm__prompt-footer">
        <DisclaimerFooter />
        <PoweredBy />
      </nav>
    </div>
  );
};
